/**
 * 🤖 صفحهٔ «وضعیت هوش مصنوعی»
 *
 * مانیتورینگ ماژول بررسی هوشمند: وضعیت کلی (کلید API، کلید اصلی، بودجه، breaker)،
 * وضعیت هر صف (چت/محصولات/ویرایش‌ها/تیکت‌ها) با آخرین اجرا و شمارنده‌ها،
 * دکمهٔ «اجرای فوری» برای تست، و تاریخچهٔ آیتم‌های بررسی‌شده.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Chip, Button, Grid, Table, TableBody,
  TableCell, TableHead, TableRow, Tabs, Tab, CircularProgress, Tooltip,
  Alert, AlertTitle, Divider, IconButton, TableContainer, Paper
} from '@mui/material';
import { useSnackbar } from 'notistack';

import RefreshIcon from '@mui/icons-material/Refresh';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SettingsIcon from '@mui/icons-material/Settings';
import ChatIcon from '@mui/icons-material/Chat';
import InventoryIcon from '@mui/icons-material/Inventory';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import GppMaybeIcon from '@mui/icons-material/GppMaybe';

import aiReviewService, {
  AiStatusResponse, AiQueueStatus, AiActivityItem, AiActivityType
} from '../services/aiReviewService';

type QueueKey = 'products' | 'edits' | 'tickets' | 'chat';
type ActivityKey = AiActivityType;

const QUEUE_META: Record<QueueKey, { label: string; icon: React.ReactNode }> = {
  products: { label: 'محصولات جدید', icon: <InventoryIcon fontSize="small" /> },
  edits: { label: 'ویرایش محصولات', icon: <EditNoteIcon fontSize="small" /> },
  tickets: { label: 'تیکت‌های پشتیبانی', icon: <SupportAgentIcon fontSize="small" /> },
  chat: { label: 'پیام‌های چت', icon: <ChatIcon fontSize="small" /> },
};

const ACTIVITY_TABS: { key: ActivityKey; label: string }[] = [
  { key: 'products', label: QUEUE_META.products.label },
  { key: 'edits', label: QUEUE_META.edits.label },
  { key: 'tickets', label: QUEUE_META.tickets.label },
  { key: 'chat', label: QUEUE_META.chat.label },
  { key: 'blocked', label: '🛡️ پیام‌های مسدودشده' },
];

const SURFACE_FA: Record<string, string> = {
  chat: 'چت خریدار',
  seller_chat: 'چت فروشنده',
  review: 'نظر محصول',
  review_reply: 'پاسخ به نظر',
  product: 'محصول',
  store: 'فروشگاه',
  other: 'سایر',
};

const MODE_FA: Record<string, string> = {
  suggest: 'فقط پیشنهاد',
  auto_approve: 'تأیید خودکار',
  auto_reply: 'پاسخ خودکار',
  flag: 'علامت‌گذاری',
};

const VERDICT_FA: Record<string, { label: string; color: 'success' | 'error' | 'warning' | 'default' | 'info' }> = {
  approve: { label: 'تأیید', color: 'success' },
  reject: { label: 'پیشنهاد رد', color: 'error' },
  unsure: { label: 'نامطمئن → کارشناس', color: 'warning' },
  clean: { label: 'سالم', color: 'success' },
  violation: { label: 'تخلف', color: 'error' },
  blocked: { label: 'مسدود شد', color: 'error' },
  muted: { label: 'مسدود + سکوت موقت', color: 'warning' },
  auto_replied: { label: 'پاسخ خودکار', color: 'success' },
  draft_saved: { label: 'پیش‌نویس ثبت شد', color: 'info' },
  needs_human: { label: 'نیازمند کارشناس', color: 'warning' },
  skipped_admin_replied: { label: 'ادمین پاسخ داده بود', color: 'default' },
};

const faDate = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fa-IR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
};

const AiReviewStatus: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [status, setStatus] = useState<AiStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [runningQueue, setRunningQueue] = useState<QueueKey | null>(null);

  const [activityTab, setActivityTab] = useState<ActivityKey>('products');
  const [activity, setActivity] = useState<AiActivityItem[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);

  const loadStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await aiReviewService.getStatus();
      setStatus(data);
    } catch {
      enqueueSnackbar('خطا در دریافت وضعیت هوش مصنوعی', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const loadActivity = useCallback(async (tab: ActivityKey, page: number) => {
    setActivityLoading(true);
    try {
      const data = await aiReviewService.getActivity(tab, page, 20);
      setActivity(data.items || []);
      setActivityTotalPages(data.totalPages || 1);
    } catch {
      enqueueSnackbar('خطا در دریافت تاریخچه', { variant: 'error' });
    } finally {
      setActivityLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadActivity(activityTab, activityPage); }, [activityTab, activityPage, loadActivity]);

  // رفرش خودکار هر ۳۰ ثانیه (بی‌صدا)
  useEffect(() => {
    const t = setInterval(() => loadStatus(true), 30000);
    return () => clearInterval(t);
  }, [loadStatus]);

  const handleRun = async (queue: QueueKey) => {
    setRunningQueue(queue);
    try {
      const { message, data } = await aiReviewService.runQueue(queue);
      const detail =
        data?.ran
          ? ` (بررسی‌شده: ${data.checked ?? data.reviewed ?? data.processed ?? 0})`
          : '';
      enqueueSnackbar(`${message}${detail}`, { variant: data?.ran ? 'success' : 'warning' });
      await loadStatus(true);
      if (queue === activityTab) await loadActivity(activityTab, activityPage);
    } catch {
      enqueueSnackbar('خطا در اجرای دستی', { variant: 'error' });
    } finally {
      setRunningQueue(null);
    }
  };

  // ── تشخیص خودکار مشکل (پاسخ به «چرا هیچ اکشنی رخ نداد؟») ──
  const problems = useMemo(() => {
    if (!status) return [];
    const list: { text: string; action?: () => void; actionLabel?: string }[] = [];
    const g = status.global;
    if (!g.apiKeyConfigured) {
      list.push({ text: 'کلید API تنظیم نشده است — AI_REVIEW_API_KEY باید در فایل env سرور بک‌اند قرار بگیرد و سرور ری‌استارت شود.' });
    }
    if (!g.baseUrlConfigured) {
      list.push({ text: 'آدرس relay تنظیم نشده (AI_REVIEW_BASE_URL). روی سرور اصلی (ایران) بدون relay ارتباط برقرار نمی‌شود؛ روی سیستم لوکال اگر VPN روشن باشد بدون relay هم کار می‌کند — برای تست، «اجرای فوری» را بزنید.' });
    }
    if (!g.masterEnabled) {
      list.push({
        text: 'کلید اصلی «فعال‌سازی کلی بررسی هوشمند» خاموش است.',
        action: () => navigate('/settings'),
        actionLabel: 'رفتن به تنظیمات',
      });
    }
    if (g.breakerOpen) {
      list.push({ text: `به‌دلیل خطاهای پیاپی، سیستم موقتاً متوقف است (تا ${faDate(g.breakerOpenUntil)}). معمولاً یعنی کلید نامعتبر، اتمام اعتبار یا قطعی relay.` });
    }
    if (g.budgetExceeded) {
      list.push({ text: `سقف بودجهٔ روزانه (${g.dailyBudgetUsd}$) پر شده — تا فردا متوقف است یا سقف را در تنظیمات بالا ببرید.` });
    }
    return list;
  }, [status, navigate]);

  const renderQueueCard = (key: QueueKey, q: AiQueueStatus) => {
    const meta = QUEUE_META[key];
    return (
      <Grid item xs={12} sm={6} lg={3} key={key}>
        <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {meta.icon}
                <Typography variant="subtitle2" fontWeight={700}>{meta.label}</Typography>
              </Box>
              <Chip
                size="small"
                label={q.enabled ? 'فعال' : 'غیرفعال'}
                color={q.enabled ? 'success' : 'default'}
                variant={q.enabled ? 'filled' : 'outlined'}
              />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
              <Chip size="small" variant="outlined" label={`حالت: ${MODE_FA[q.mode] || q.mode}`} />
              <Chip size="small" variant="outlined" label={`هر ${q.intervalMinutes} دقیقه`} />
              <Chip size="small" variant="outlined" label={`${q.batchSize}تایی`} />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1}>{q.pending}</Typography>
                <Typography variant="caption" color="text.secondary">در انتظار</Typography>
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1}>{q.processed}</Typography>
                <Typography variant="caption" color="text.secondary">بررسی‌شده</Typography>
              </Box>
              {typeof q.violations === 'number' && (
                <Box>
                  <Typography variant="h6" fontWeight={800} lineHeight={1} color="error.main">{q.violations}</Typography>
                  <Typography variant="caption" color="text.secondary">تخلف</Typography>
                </Box>
              )}
              {typeof q.autoReplied === 'number' && (
                <Box>
                  <Typography variant="h6" fontWeight={800} lineHeight={1} color="success.main">{q.autoReplied}</Typography>
                  <Typography variant="caption" color="text.secondary">پاسخ خودکار</Typography>
                </Box>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" display="block">
              آخرین اجرا: {faDate(q.lastRunAt)}
            </Typography>
            {q.lastResult && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ direction: 'ltr', textAlign: 'right' }}>
                {q.lastResult.error
                  ? `❌ ${String(q.lastResult.error).slice(0, 60)}`
                  : `✓ ${faDate(q.lastResult.at)}`}
              </Typography>
            )}

            <Button
              fullWidth
              size="small"
              variant="outlined"
              sx={{ mt: 1.5 }}
              startIcon={runningQueue === key ? <CircularProgress size={14} /> : <PlayArrowIcon />}
              disabled={runningQueue !== null}
              onClick={() => handleRun(key)}
            >
              اجرای فوری
            </Button>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const StatusDot = ({ ok, label }: { ok: boolean; label: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {ok
        ? <CheckCircleIcon sx={{ fontSize: 18 }} color="success" />
        : <CancelIcon sx={{ fontSize: 18 }} color="error" />}
      <Typography variant="body2">{label}</Typography>
    </Box>
  );

  if (loading && !status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  const g = status?.global;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToyIcon color="primary" />
          <Typography variant="h5" fontWeight={800}>وضعیت هوش مصنوعی</Typography>
          {g && (
            <Chip
              size="small"
              icon={g.operational ? <CheckCircleIcon /> : <WarningAmberIcon />}
              label={g.operational ? 'در حال کار' : 'غیرفعال'}
              color={g.operational ? 'success' : 'error'}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="تنظیمات هوش مصنوعی">
            <IconButton onClick={() => navigate('/settings')}><SettingsIcon /></IconButton>
          </Tooltip>
          <Tooltip title="به‌روزرسانی">
            <IconButton onClick={() => loadStatus()}><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Diagnosis */}
      {problems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          <AlertTitle>چرا بررسی خودکار انجام نمی‌شود؟</AlertTitle>
          {problems.map((p, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="body2">• {p.text}</Typography>
              {p.action && (
                <Button size="small" onClick={p.action}>{p.actionLabel}</Button>
              )}
            </Box>
          ))}
        </Alert>
      )}

      {/* Global status */}
      {g && (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', py: 1.5 }}>
            <StatusDot ok={g.apiKeyConfigured} label="کلید API" />
            <StatusDot ok={g.baseUrlConfigured} label="آدرس Relay" />
            <StatusDot ok={g.masterEnabled} label="کلید اصلی (تنظیمات)" />
            <StatusDot ok={!g.breakerOpen} label="اتصال سالم" />
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="body2" fontWeight={700}>
                مصرف امروز: {g.spentTodayUsd}$ از {g.dailyBudgetUsd || '∞'}$
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {g.budgetExceeded ? 'سقف بودجه پر شده — تا فردا متوقف' : 'بودجهٔ روزانه'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Queues */}
      {status && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {(['products', 'edits', 'tickets', 'chat'] as QueueKey[]).map((k) =>
            renderQueueCard(k, status.queues[k])
          )}
        </Grid>
      )}

      {/* 🛡️ سیاست تماس — مسدودسازی فوری (بدون AI) */}
      {status?.contactPolicy && (
        <Card variant="outlined" sx={{ mb: 3, borderRadius: 2, borderColor: status.contactPolicy.enabled ? 'success.light' : 'warning.light' }}>
          <CardContent sx={{ py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <GppMaybeIcon fontSize="small" color={status.contactPolicy.enabled ? 'success' : 'warning'} />
                <Typography variant="subtitle2" fontWeight={700}>
                  سیاست تماس — مسدودسازی فوری آیدی تلگرام / شماره / لینک پیام‌رسان
                </Typography>
                <Chip
                  size="small"
                  label={status.contactPolicy.enabled ? 'فعال' : 'غیرفعال'}
                  color={status.contactPolicy.enabled ? 'success' : 'default'}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={() => { setActivityTab('blocked'); setActivityPage(1); }}>
                  مشاهدهٔ پیام‌های مسدودشده
                </Button>
                <Button size="small" startIcon={<SettingsIcon />} onClick={() => navigate('/settings')}>
                  تنظیمات
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1}>{status.contactPolicy.last24h}</Typography>
                <Typography variant="caption" color="text.secondary">مسدود در ۲۴ ساعت</Typography>
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1}>{status.contactPolicy.total}</Typography>
                <Typography variant="caption" color="text.secondary">کل مسدودشده‌ها</Typography>
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1} color="warning.main">{status.contactPolicy.mutedNow}</Typography>
                <Typography variant="caption" color="text.secondary">در سکوت موقت</Typography>
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} lineHeight={1} color="error.main">{status.contactPolicy.offenders7d}</Typography>
                <Typography variant="caption" color="text.secondary">کاربر متخلف (۷ روز)</Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Chip size="small" variant="outlined" label={`آستانه: ${status.contactPolicy.strikeThreshold} اخطار / ${status.contactPolicy.strikeWindowHours} ساعت`} />
                <Chip size="small" variant="outlined" label={status.contactPolicy.muteMinutes > 0 ? `سکوت: ${status.contactPolicy.muteMinutes} دقیقه` : 'بدون سکوت'} />
                <Tooltip title={status.contactPolicy.blockRules.join(', ')}>
                  <Chip size="small" variant="outlined" label={`${status.contactPolicy.blockRules.length} قاعدهٔ مسدودکننده`} />
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Activity */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activityTab}
            onChange={(_e, v) => { setActivityTab(v); setActivityPage(1); }}
            variant="scrollable"
          >
            {ACTIVITY_TABS.map((t) => (
              <Tab key={t.key} value={t.key} label={t.label} />
            ))}
          </Tabs>
        </Box>

        {activityTab === 'blocked' && (
          <Alert severity="info" sx={{ m: 1.5, borderRadius: 2 }}>
            این پیام‌ها هرگز به طرف مقابل نرسیده‌اند: در لحظهٔ ارسال، به‌دلیل آیدی/شماره/لینک پیام‌رسان رد شده و به فرستنده پیام راهنما نمایش داده شده است.
            ستون «اخطار» شمارهٔ تخلف در پنجرهٔ زمانی است؛ با رسیدن به آستانه، کاربر موقتاً ساکت و به سوپرادمین‌ها اطلاع داده می‌شود.
          </Alert>
        )}

        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{activityTab === 'blocked' ? 'متن مسدودشده' : 'آیتم'}</TableCell>
                <TableCell align="center">{activityTab === 'blocked' ? 'وضعیت' : 'نتیجهٔ AI'}</TableCell>
                <TableCell align="center">{activityTab === 'blocked' ? 'اخطار' : 'اطمینان'}</TableCell>
                <TableCell>{activityTab === 'blocked' ? 'قواعد / محل' : 'توضیح'}</TableCell>
                <TableCell align="center">{activityTab === 'blocked' ? 'زمان' : 'زمان بررسی'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activityLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : activity.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      هنوز موردی توسط AI بررسی نشده است
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                activity.map((item) => {
                  const verdictKey = item.aiReview?.verdict || item.aiReview?.action || item.status;
                  const v = VERDICT_FA[verdictKey] || { label: verdictKey || '—', color: 'default' as const };
                  const reasons: string[] = item.aiReview?.reasons ||
                    (item.aiReview?.reason ? [item.aiReview.reason] : []) ||
                    (item.aiReview?.note ? [item.aiReview.note] : []);
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.image && (
                            <Box
                              component="img"
                              src={item.image}
                              alt=""
                              sx={{ width: 34, height: 34, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
                            />
                          )}
                          <Box sx={{ minWidth: 0 }}>
                            <Tooltip title={item.title}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>{item.title}</Typography>
                            </Tooltip>
                            {item.sender && (
                              <Typography variant="caption" color="text.secondary">
                                فرستنده: {item.sender}
                                {item.senderIsSeller ? ' (فروشنده)' : ''}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={v.label} color={v.color} />
                      </TableCell>
                      <TableCell align="center">
                        {activityTab === 'blocked'
                          ? (
                            <Box>
                              <Typography variant="body2" fontWeight={700}>#{item.aiReview?.strikeNo ?? 1}</Typography>
                              {item.aiReview?.escalated && (
                                <Typography variant="caption" color="error.main">اطلاع به ادمین</Typography>
                              )}
                            </Box>
                          )
                          : typeof item.aiReview?.confidence === 'number'
                            ? `${item.aiReview.confidence}٪`
                            : '—'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Tooltip title={reasons.join(' | ')}>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {reasons.join('، ') || (item.aiReview?.note || '—')}
                          </Typography>
                        </Tooltip>
                        {activityTab === 'blocked' && item.surface && (
                          <Chip size="small" variant="outlined" label={SURFACE_FA[item.surface] || item.surface} sx={{ mt: 0.25 }} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption">
                          {faDate(item.aiReview?.reviewedAt || item.aiReview?.checkedAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, py: 1.5 }}>
          <Button
            size="small"
            disabled={activityPage <= 1 || activityLoading}
            onClick={() => setActivityPage((p) => p - 1)}
          >
            قبلی
          </Button>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>
            صفحهٔ {activityPage} از {activityTotalPages}
          </Typography>
          <Button
            size="small"
            disabled={activityPage >= activityTotalPages || activityLoading}
            onClick={() => setActivityPage((p) => p + 1)}
          >
            بعدی
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default AiReviewStatus;
